import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import RacingThemeRoot from '../RacingThemeRoot/RacingThemeRoot';
import RacingThemeShopPurchasePanel from '../RacingThemeShopPurchasePanel/RacingThemeShopPurchasePanel';
import RacingThemeBookBookingPanel from '../RacingThemeBookBookingPanel/RacingThemeBookBookingPanel';
import RacingThemePartnerInquiryPanel from '../RacingThemePartnerInquiryPanel/RacingThemePartnerInquiryPanel';
import RacingThemeListingBreadcrumbs from './RacingThemeListingBreadcrumbs';
import RacingThemeMemberIdentity from '../RacingThemeMemberIdentity/RacingThemeMemberIdentity';
import RacingThemeListingGallery from './RacingThemeListingGallery';
import RacingThemeListingQuickInfo from './RacingThemeListingQuickInfo';
import RacingThemeListingAccordions from './RacingThemeListingAccordions';
import RacingThemeListingMoreFrom from './RacingThemeListingMoreFrom';
import RacingThemeListingReviews from './RacingThemeListingReviews';
import css from './RacingThemeListingPage.module.css';

const MockVariantCtaPanel = ({ variant, content }) => {
  if (variant === 'shop' && content.purchase) {
    return <RacingThemeShopPurchasePanel {...content.purchase} />;
  }
  if (variant === 'book' && content.booking) {
    return <RacingThemeBookBookingPanel {...content.booking} />;
  }
  if (variant === 'partner' && content.inquiry) {
    return <RacingThemePartnerInquiryPanel {...content.inquiry} />;
  }
  return null;
};

/**
 * Shared Shop/Book/Partner listing detail shell.
 * Sharetribe sections (custom fields, map, author) must stay outside RacingThemeRoot
 * — root uses `all: revert` which strips Sharetribe component CSS.
 */
const RacingThemeListingPage = props => {
  const {
    className,
    rootClassName,
    content,
    ctaSlot = null,
    bannerSlot = null,
    detailsSlot = null,
    afterMain = null,
    onMessage,
    onFavorite,
    isFavorite = false,
    favoriteId = null,
    isFavoriteListingFn = null,
    onToggleFavorite = null,
  } = props;
  const classes = classNames(css.root, rootClassName, className);
  const variant = content?.variant || 'shop';
  const isShop = variant === 'shop';
  const showMockQuickInfo = isShop && !ctaSlot && content.quickInfo;
  const useLiveCta = !!ctaSlot;
  const beforeDetails = (content.accordions || []).filter(a => a.placement !== 'afterDetails');
  const afterDetails = (content.accordions || []).filter(a => a.placement === 'afterDetails');
  const accordionItems = [
    ...beforeDetails,
    detailsSlot
      ? {
          id: 'details',
          title: 'Details',
          icon: 'details',
          body: detailsSlot,
        }
      : null,
    ...afterDetails,
  ].filter(Boolean);

  const cta = useLiveCta ? (
    <div className={css.ctaColumn}>{ctaSlot}</div>
  ) : (
    <RacingThemeRoot className={css.ctaRoot}>
      <div className={css.ctaColumn}>
        <MockVariantCtaPanel variant={variant} content={content} />
      </div>
    </RacingThemeRoot>
  );

  return (
    <div className={classNames(css.pageShell, classes)} data-variant={variant} data-department={variant}>
      <div className={css.inner}>
        <RacingThemeRoot>
          <RacingThemeListingBreadcrumbs items={content.breadcrumbs} />
          <RacingThemeMemberIdentity
            identity={content.identity}
            onMessage={onMessage}
            showMessage
            onFavorite={onFavorite}
            isFavorite={isFavorite}
            favoriteId={favoriteId}
          />
        </RacingThemeRoot>

        {bannerSlot ? <div className={css.bannerSlot}>{bannerSlot}</div> : null}

        <div className={css.mainRow}>
          <RacingThemeRoot className={css.galleryRoot}>
            <RacingThemeListingGallery items={content.gallery?.items} />
          </RacingThemeRoot>
          {cta}
        </div>

        <RacingThemeRoot>
          {showMockQuickInfo ? <RacingThemeListingQuickInfo items={content.quickInfo} /> : null}
          <RacingThemeListingAccordions items={accordionItems} />
          {content.moreFrom ? (
            <RacingThemeListingMoreFrom
              title={content.moreFrom.title}
              viewAllLabel={content.moreFrom.viewAllLabel}
              viewAllHref={content.moreFrom.viewAllHref}
              listings={content.moreFrom.listings}
              isFavoriteFn={isFavoriteListingFn}
              onToggleFavorite={onToggleFavorite}
            />
          ) : null}
          {content.reviews ? (
            <RacingThemeListingReviews
              average={content.reviews.average}
              count={content.reviews.count}
              distribution={content.reviews.distribution}
              items={content.reviews.items}
            />
          ) : null}
        </RacingThemeRoot>

        {afterMain ? <div className={css.sharetribeSections}>{afterMain}</div> : null}
      </div>
    </div>
  );
};

RacingThemeListingPage.defaultProps = {
  className: null,
  rootClassName: null,
  ctaSlot: null,
  bannerSlot: null,
  detailsSlot: null,
  afterMain: null,
  onMessage: () => {},
  onFavorite: null,
  isFavorite: false,
  favoriteId: null,
  isFavoriteListingFn: null,
  onToggleFavorite: null,
};

RacingThemeListingPage.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  content: PropTypes.object.isRequired,
  ctaSlot: PropTypes.node,
  bannerSlot: PropTypes.node,
  detailsSlot: PropTypes.node,
  afterMain: PropTypes.node,
  onMessage: PropTypes.func,
  onFavorite: PropTypes.func,
  isFavorite: PropTypes.bool,
  favoriteId: PropTypes.string,
  isFavoriteListingFn: PropTypes.func,
  onToggleFavorite: PropTypes.func,
};

export default RacingThemeListingPage;
